import { metrics } from '@seek/metrics-js';
import { useTranslations } from '@vocab/react';
import {
  Button,
  MenuRenderer,
  Box,
  MenuItem,
  IconCopy,
  useToast,
  Dialog,
} from 'braid-design-system';
import { useCallback, useState } from 'react';

import { LoadingItem } from 'src/components/LoadingBlock/LoadingBlock';
import { useGetShareableProfileStatus } from 'src/components/Profile/ProfileVisibilityV2/data';
import { getProfileSharedStatus } from 'src/modules/analytics';
import { useAnalytics } from 'src/modules/analytics/AnalyticsProvider';

import translations from './.vocab';
import { ConsentAndCreate } from './ActionDialog/ConsentAndCreate';
import { InactiveReminder } from './ActionDialog/InactiveReminder';
import { Ready } from './ActionDialog/Ready';

export const ShareableProfileShareButton = () => {
  const { t } = useTranslations(translations);
  const { shareableProfileShareLinkClicked } = useAnalytics();
  const [showActionDialog, setShowActionDialog] = useState(false);
  const showToast = useToast();

  const { isShareableProfileActive, shareableProfileUrl, loading, error } =
    useGetShareableProfileStatus();

  const isShareableProfileCreated = Boolean(shareableProfileUrl);

  const sendTrackingLinkEvent = useCallback(
    (eventName: 'profile_link_copy_pressed') => {
      shareableProfileShareLinkClicked({
        eventName,
        currentPage: 'profile',
        actionOrigin: 'main card',
        profileSharedStatus: getProfileSharedStatus({
          isShareableProfileCreated,
          isShareableProfileActive,
        }),
      });
    },
    [
      isShareableProfileActive,
      isShareableProfileCreated,
      shareableProfileShareLinkClicked,
    ],
  );

  const copyLinkFn = useCallback(() => {
    navigator.clipboard.writeText(shareableProfileUrl || '');
    showToast({
      key: 'link-copied',
      tone: 'positive',
      message: 'Link copied',
    });
    metrics.count('shareable_profile_url_copied');
  }, [shareableProfileUrl, showToast]);

  const onClickCopyLink = () => {
    sendTrackingLinkEvent('profile_link_copy_pressed');
    if (!isShareableProfileActive) {
      setShowActionDialog(true);
    } else {
      copyLinkFn();
    }
  };

  const getActionDialog = useCallback(() => {
    if (showActionDialog) {
      if (!isShareableProfileCreated) {
        return {
          id: 'consent-and-create-btn-dialog',
          title: t('Create a shareable link'),
          component: <ConsentAndCreate />,
        };
      }
      return isShareableProfileActive
        ? {
            id: 'shareable-profile-active-dialog',
            title: t('Your profile is ready to share!'),
            component: <Ready copyLinkFn={copyLinkFn} />,
          }
        : {
            id: 'shareable-profile-inactive-dialog',
            title: t('Turn shared profile on'),
            component: (
              <InactiveReminder closeFn={() => setShowActionDialog(false)} />
            ),
          };
    }
  }, [
    showActionDialog,
    isShareableProfileCreated,
    isShareableProfileActive,
    t,
    copyLinkFn,
  ]);

  const actionDialog = getActionDialog();

  const ShareButton = () =>
    !isShareableProfileCreated ? (
      <Button
        id="create-and-share-btn"
        data={{ automation: 'create-and-share-btn' }}
        variant="ghost"
        tone="neutral"
        onClick={() => setShowActionDialog(true)}
      >
        {t('Share')}
      </Button>
    ) : (
      <MenuRenderer
        data={{ automation: 'share-shareable-profile-btn' }}
        offsetSpace="small"
        trigger={(triggerProps) => (
          <Box userSelect="none" cursor="pointer" {...triggerProps}>
            <Button variant="ghost" tone="neutral">
              {t('Share')}
            </Button>
          </Box>
        )}
      >
        <MenuItem
          id="copy-link-menu"
          data={{ automation: 'copy-link-menu' }}
          icon={<IconCopy />}
          onClick={onClickCopyLink}
        >
          {t('Copy link')}
        </MenuItem>
      </MenuRenderer>
    );

  return loading || error ? (
    <LoadingItem type="button" />
  ) : (
    <>
      <ShareButton />
      {actionDialog && (
        <Dialog
          id={actionDialog.id}
          data={{ automation: actionDialog.id }}
          title={actionDialog.title}
          width="xsmall"
          open={showActionDialog}
          onClose={() => setShowActionDialog(false)}
          closeLabel={t('Close')}
        >
          {actionDialog.component}
        </Dialog>
      )}
    </>
  );
};
